/**
 * Every room has a name, and a room nobody named gets a funny one.
 *
 * A room used to be a uuid to everybody who was not an operator: the roster names the rooms a
 * deployment declares, but an overflow room the matchmaker invents had nothing but its id, and
 * an id is not something a player can say out loud, remember, or tell a friend to meet them in.
 * This is the other half — one pure function from a room's id to a name it keeps for its whole
 * life.
 *
 * ## Seeded, not random
 *
 * `roomNameFor` hashes the id and indexes two word lists with it. Same id, same name, on every
 * process that asks — which is what lets the gateway put it on `welcome`, the host put it on a
 * `RoomSummary`, and the API server fill it in for a worker too old to send one, without any of
 * the three having to agree about anything but this file. It is also why nothing here draws from
 * the room's own RNG: the simulation's seeded stream decides where pellets land and what bots do,
 * and a name is not allowed to consume a slot in it (see CLAUDE.md, "Nothing here draws from the
 * room's stream").
 *
 * ## Not the roster's names
 *
 * A declared room already has a name an operator typed (`room_roster.name`) and keeps it — this
 * is only ever the fallback for a room that was never declared. `rosterRoomNameSchema` and the
 * name on the wire share `ROOM_NAME_MAX_LENGTH`, so a name an operator may save is a name that
 * fits in a `welcome` frame.
 */

/**
 * The longest a room name may be, typed or generated.
 *
 * The bound the roster has always applied to a name an operator types, shared with the protocol
 * because both ends now carry one: a name too long for `roomInfoSchema` would be a declaration
 * that saved and then could not be joined.
 */
export const ROOM_NAME_MAX_LENGTH = 48;

/**
 * The first word. Family-friendly and a bit silly, because the point of the feature is that a
 * room reads as a place rather than as a record in a table.
 */
export const ROOM_NAME_ADJECTIVES = [
  'Soggy',
  'Grumpy',
  'Wobbly',
  'Salty',
  'Sleepy',
  'Crispy',
  'Cheeky',
  'Rowdy',
  'Bouncy',
  'Dizzy',
  'Greedy',
  'Peckish',
  'Chunky',
  'Slippery',
  'Squeaky',
  'Frosty',
  'Rusty',
  'Jolly',
] as const;

/**
 * The second word, and it is a *place*: a room is somewhere a player goes, so every entry reads
 * as one. Two words each, which is what makes "Soggy Dot Saloon" a name rather than a label.
 */
export const ROOM_NAME_NOUNS = [
  'Nibble Pit',
  'Dot Saloon',
  'Chomp Chasm',
  'Pellet Parlour',
  'Snack Shack',
  'Gobble Gulch',
  'Munch Meadow',
  'Crumb Canyon',
  'Bubble Basin',
  'Blob Bazaar',
  'Morsel Market',
  'Gulp Gully',
  'Feast Field',
  'Wobble Works',
  'Snap Station',
  'Slurp Springs',
] as const;

/**
 * What a repeat of the same base name is marked with, in order.
 *
 * Roman numerals rather than "(2)": a second Soggy Dot Saloon is a sequel, not a duplicate row.
 * Past the table it is the plain number, which is a case a host would have to hold a dozen rooms
 * that all hashed the same way to reach.
 */
const ROOM_NAME_ORDINALS = ['II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];

/** FNV-1a, 32-bit. Small, stable across processes and versions, and not a security primitive. */
function hashSeed(seed: string): number {
  let hash = 0x811c_9dc5;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    // The FNV prime, by shifts, because `hash * 16777619` loses the low bits to float precision.
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
  }
  return hash >>> 0;
}

/**
 * The name a room with this id is called, unless somebody declared one for it.
 *
 * `ROOM_NAME_ADJECTIVES.length * ROOM_NAME_NOUNS.length` combinations — 288 of them — off one
 * hash: the low end of it picks the adjective and what is left above picks the noun, so two ids
 * that differ only in their last character do not get the same noun.
 */
export function roomNameFor(seed: string): string {
  const hash = hashSeed(seed);
  const adjective = ROOM_NAME_ADJECTIVES[hash % ROOM_NAME_ADJECTIVES.length];
  const noun =
    ROOM_NAME_NOUNS[Math.floor(hash / ROOM_NAME_ADJECTIVES.length) % ROOM_NAME_NOUNS.length];
  return `${adjective} ${noun}`;
}

/**
 * The same name, disambiguated against the names already in use.
 *
 * Deterministic in both halves: the base is the hash's, and a collision walks a fixed list of
 * marks rather than reaching for a random suffix or for a slice of the id — a player must never
 * be shown a fragment of a uuid, which is the whole point of naming rooms. Terminates because
 * every ordinal produces a name the one before it did not.
 *
 * `isTaken` is the caller's own set: a host disambiguates against the rooms it is holding, which
 * is the scope a collision can actually be seen in — two rooms on two hosts are never listed
 * against each other for a player, and the admin console draws the host beside the room.
 */
export function uniqueRoomName(seed: string, isTaken: (name: string) => boolean): string {
  const base = roomNameFor(seed);
  if (!isTaken(base)) return base;

  for (let ordinal = 2; ; ordinal += 1) {
    const candidate = `${base} ${ROOM_NAME_ORDINALS[ordinal - 2] ?? String(ordinal)}`;
    if (!isTaken(candidate)) return candidate;
  }
}
